import { get } from 'lodash';
import { useCallback, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from 'src/AppContext';
import { useCheckbox } from 'src/components/Checkbox';
import { useFormInputEmail, useFormInputPassword } from 'src/components/FormInput';
import useCustomHistory from 'src/hooks/useCustomHistory';
import { useModal } from 'src/ModalContext';
import { authorizeBixby, userLogin } from 'src/services/api';
import { useUser } from 'src/UserContext';
import LoginView from '../View';

// const CreateHiddenElement = (queryString: Record<string, string>) => {
//   return Object.entries(queryString).map((values) => {
//     return <input key={values[0]} name={values[0]} value={values[1]} type="hidden" required />;
//   });
// };

export default function LoginBixby(): JSX.Element {
  const { t } = useTranslation();
  const history = useCustomHistory();
  const location = useLocation();
  const emailInput = useFormInputEmail();
  const passwordInput = useFormInputPassword();
  const autoLoginCheckbox = useCheckbox({
    name: 'auto',
    component: <label className="N_12_R text-brown-grey">{t('login/autologin_title')}</label>,
  });
  const { login, logout } = useUser();
  const dispatch = useAppDispatch();
  const { showModal } = useModal();

  // QueryString 값 추출
  const getQueryStringValues = useCallback<() => Record<string, string>>(() => {
    return decodeURI(location.search)
      .replace('?', '')
      .split('&')
      .map((param) => param.split('='))
      .reduce<{
        [key: string]: string;
      }>((values, [key, value]) => {
        values[key] = value;
        return values;
      }, {});
  }, [location]);

  // 폼 sumit -> ajax 요청으로 대체
  const onSubmit = useCallback(
    async (e) => {
      userLogin({
        email: e.target.email.value.trim(),
        password: e.target.password.value,
      })
        .then((res) => {
          login(res);
          onAuthorize(e);
        })
        .catch(() => {
          logout();
          const code = get(e, 'response.data.code');
          switch (code) {
            case 'USER_NOT_FOUND':
              showModal({
                message: t('login/login_fail'),
                success: () => {
                  return;
                },
              });
              break;
            case 'NOT_VERIFIED_USER':
              history.push('/email-link', e.response.data.data);
              break;
          }
        });
    },
    [history, autoLoginCheckbox.checked, emailInput.value, passwordInput.value],
  );

  const onAuthorize = useCallback(
    (e) => {
      authorizeBixby({
        email: e.target.email.value.trim(),
        password: e.target.password.value,
        ...getQueryStringValues(),
      })
        .then((res) => {
          window.location.replace(res.redirect_uri);
        })
        .catch((e) => {
          const code = get(e, 'response.data.code');
          switch (code) {
            case 'USER_NOT_FOUND':
              showModal({
                message: t('login/login_fail'),
                success: () => {
                  return;
                },
              });
              break;
            case 'NOT_VERIFIED_USER':
              history.push('/email-link', e.response.data.data);
              break;
          }
        });
    },
    [history],
  );

  useLayoutEffect(() => {
    dispatch({ type: 'SET_GNB_LEFT', name: 'back' });
    dispatch({
      type: 'SET_GNB_LEFT_CALLBACK',
      cb: () => {
        history.push('/');
      },
    });
    dispatch({ type: 'SET_GNB_RIGHT', name: 'hidden' });
    dispatch({ type: 'SET_GNB_CENTER', isShow: false });
    return () => {
      dispatch({ type: 'RESET_GNB_LEFT_CALLBACK' });
      dispatch({ type: 'RESET_GNB_LEFT' });
      dispatch({ type: 'RESET_GNB_RIGHT' });
      dispatch({ type: 'SET_GNB_CENTER', isShow: true });
    };
  }, []);

  return <LoginView isShowAutoLogin={false} onSubmit={onSubmit} />;
}
